<div class="flex flex-col w-full h-full">
  <h1 class="mb-8 text-light-300 text-3xl font-medium">
    Wallet <span class="text-xl text-red-100">(Demo Mode)</span>
  </h1>

  <div class="flex flex-col gap-12 w-full h-full">
    <div class="flex flex-col gap-12 w-full lg:flex-row lg:gap-6">
      <!-- Wallet balance -->
      <div class="flex flex-col basis-1/2">
        <p-card class="w-full">
          <div
            class="flex flex-col sm:flex-row gap-4 justify-between w-full py-0 px-0 sm:py-2 sm:px-4 overflow-hidden"
          >
            <div class="flex flex-col gap-2 justify-between">
              <span class="text-xl text-light-100">Cash balance</span>

              @if(_walletService.isWalletLoading$ | async){
              <ng-container *ngTemplateOutlet="loadingSkeletonBalance" />
              } @else {
              <span class="font-extrabold text-4xl text-light-300">
                {{ wallet?.cashBalance | customCurrency : currency }}
              </span>
              }
            </div>

            <div class="flex flex-col gap-4 basis-1/4">
              <p-fluid>
                <p-button
                  severity="primary"
                  fluid="true"
                  (onClick)="onDeposit()"
                >
                  Deposit
                </p-button>
              </p-fluid>

              <p-fluid>
                <p-button
                  severity="primary"
                  variant="outlined"
                  fluid="true"
                  [disabled]="!wallet?.isCashBalanceWithdrawable"
                  (onClick)="onWithdraw()"
                >
                  Withdraw
                </p-button>
              </p-fluid>
            </div>
          </div>
        </p-card>
      </div>

      <!-- Rewards balance -->
      <div class="flex flex-col basis-1/2">
        <p-card class="w-full">
          <div
            class="flex flex-row gap-4 justify-between items-center w-full py-2 px-4"
          >
            <div class="flex flex-col gap-8 justify-between">
              <div class="flex flex-row gap-1 items-center text-light-100">
                <span class="text-xl">Rewards balance</span>
                <svg-icon
                  src="assets/icons/info.svg"
                  svgClass="h-4 w-4"
                  (click)="isTouchDevice && popoverRB.toggle($event)"
                  (mouseenter)="!isTouchDevice && popoverRB.show($event)"
                  (mouseleave)="!isTouchDevice && popoverRB.hide()"
                />

                <p-popover #popoverRB>
                  <p class="w-72 text-light-0 text-base">
                    This number shows your current rewards balance. It includes
                    all cashback rewards earned from purchases, referral
                    bonuses, and any other promotional rewards that have not
                    been redeemed yet. This balance can be used to invest in new
                    properties but cannot be withdrawn as cash.
                  </p>
                </p-popover>
              </div>

              @if(_walletService.isWalletLoading$ | async){
              <ng-container *ngTemplateOutlet="loadingSkeletonBalance" />
              } @else {
              <span class="font-extrabold text-4xl text-light-300">
                {{ wallet?.rewardsBalance | customCurrency : currency }}
              </span>
              }
            </div>

            <svg-icon
              src="assets/icons/star.svg"
              svgClass="h-20 w-20 text-blue-100"
            />
          </div>
        </p-card>
      </div>
    </div>

    <!-- Wallet transactions -->
    <div class="flex flex-col gap-4">
      <h3 class="font-bold text-xl text-light-300">Transactions</h3>

      <p-card class="w-full">
        @if(_walletService.isWalletTransactionsLoading$ | async){
        <ng-container *ngTemplateOutlet="loadingSkeletonTable"></ng-container>
        } @else {
        <p-table
          [value]="walletTransactions"
          [scrollable]="true"
          scrollHeight="345px"
          stripedRows
          [tableStyle]="{ 'min-width': '50rem' }"
          size="large"
        >
          <ng-template #header>
            <tr>
              <th>Type</th>
              <th>Status</th>
              <th>Date</th>
              <th>Wallet</th>
              <th>Amount</th>
            </tr>
          </ng-template>
          <ng-template #body let-transaction>
            <tr>
              <td>
                <!-- # Transaction type -->
                @switch (transaction.type) {
                <!-- CASHBACK -->
                @case (Transaction.CASHBACK) {
                <ng-container
                  [ngTemplateOutlet]="transactionsTypeRow"
                  [ngTemplateOutletContext]="{
                    icon: 'cash',
                    label: 'Cashback'
                  }"
                />
                }
                <!-- DEPOSIT -->
                @case (Transaction.DEPOSIT) {
                <ng-container
                  [ngTemplateOutlet]="transactionsTypeRow"
                  [ngTemplateOutletContext]="{
                    icon: 'wallet',
                    label: 'Deposit'
                  }"
                />
                }
                <!-- INVESTMENT -->
                @case (Transaction.INVESTMENT) {
                <ng-container
                  [ngTemplateOutlet]="transactionsTypeRow"
                  [ngTemplateOutletContext]="{
                    icon: 'house-2',
                    label: 'Investment'
                  }"
                />
                }
                <!-- REFERRER GIFT -->
                @case (Transaction.REFERRER_GIFT) {
                <ng-container
                  [ngTemplateOutlet]="transactionsTypeRow"
                  [ngTemplateOutletContext]="{
                    icon: 'star',
                    label: 'Referrer Gift'
                  }"
                />
                }
                <!-- RENTAL PAID -->
                @case (Transaction.RENTAL_PAID) {
                <ng-container
                  [ngTemplateOutlet]="transactionsTypeRow"
                  [ngTemplateOutletContext]="{
                    icon: 'coins',
                    label: 'Rental Paid'
                  }"
                />
                }
                <!-- WITHDRAWAL -->
                @case (Transaction.WITHDRAWAL) {
                <ng-container
                  [ngTemplateOutlet]="transactionsTypeRow"
                  [ngTemplateOutletContext]="{
                    icon: 'bank',
                    label: 'Withdrawal'
                  }"
                />
                }
                <!--  -->
                }
              </td>
              <td>
                <!-- # Transaction Status -->
                @switch (transaction.status){
                <!-- PROCESSING -->
                @case (TransactionStatus.PROCESSING) { Processing }
                <!-- COMPLETED -->
                @case (TransactionStatus.COMPLETED) { Completed }
                <!--  -->
                }
              </td>
              <td>{{ transaction.date | date : "d MMMM y" }}</td>
              <td>
                <!-- # Transaction wallet type -->
                @switch (transaction.wallet) {
                <!-- CASH_BALANCE -->
                @case (WalletType.CASH_BALANCE) { Cash balance }
                <!-- REWARDS_BALANCE -->
                @case (WalletType.REWARDS_BALANCE) { Rewards balance }
                <!--  -->
                }
              </td>
              <td class="font-semibold">
                @if(transaction.type === Transaction.WITHDRAWAL ||
                transaction.type === Transaction.INVESTMENT) {
                <!--  -->
                -{{ transaction.amount | customCurrency : currency }}
                <!--  -->
                }@else {
                <!--  -->
                +{{ transaction.amount | customCurrency : currency }}
                <!--  -->
                }
              </td>
            </tr>
          </ng-template>
          <ng-template #emptymessage>
            <tr>
              <td colspan="5">
                <div
                  class="flex flex-col gap-4 items-center justify-center text-base text-light-300 p-5"
                >
                  <svg-icon src="assets/icons/clock.svg" svgClass="h-8 w-8" />
                  <p>No transactions yet</p>
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>

        <div class="px-4"></div>
        }
      </p-card>
    </div>

    <!-- Cards / Bank accounts -->
    <div class="flex flex-col gap-12 w-full lg:flex-row lg:gap-4 mb-5">
      <!-- Hide this for now -->
      <!-- <div class="flex basis-1/2">
        <app-cards-list class="w-full" />
      </div> -->

      <div class="flex flex-col gap-4 w-full">
        <app-bank-accounts-list class="w-full" />
      </div>
    </div>
  </div>
</div>

<ng-template #loadingSkeletonBalance>
  <p-skeleton height="40px" width="150px" />
</ng-template>

<ng-template #loadingSkeletonTable>
  <p-table
    [value]="[1, 2, 3, 4, 5]"
    [scrollable]="true"
    scrollHeight="345px"
    stripedRows
    [tableStyle]="{ 'min-width': '50rem' }"
    size="large"
  >
    <ng-template #header>
      <tr>
        <th>Type</th>
        <th>Status</th>
        <th>Date</th>
        <th>Wallet</th>
        <th>Amount</th>
      </tr>
    </ng-template>
    <ng-template #body let-transaction>
      <tr>
        <td>
          <p-skeleton height="24px" width="100px" />
        </td>
        <td>
          <p-skeleton height="24px" width="80px" />
        </td>
        <td>
          <p-skeleton height="24px" width="150px" />
        </td>
        <td>
          <p-skeleton height="24px" width="120px" />
        </td>
        <td>
          <p-skeleton height="24px" width="80px" />
        </td>
      </tr>
    </ng-template>
  </p-table>
</ng-template>

<ng-template #transactionsTypeRow let-icon="icon" let-label="label">
  <div class="flex flex-row gap-2 items-center">
    <svg-icon [src]="'assets/icons/' + icon + '.svg'" svgClass="h-5 w-5" />
    <span class="text-light-300">{{ label }}</span>
  </div>
</ng-template>
